import { Dispatch } from '@reduxjs/toolkit';

import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { AccountStatus } from 'dashboard/models/account-status';
import { AccountType } from 'dashboard/models/account-type';
import {
  addAccountStatuses,
  setAccountStatuses,
  updateAccountStatus,
  updateDefaultGraphQueryTemplate,
} from 'dashboard/redux/actions/account-statuses';
import { DashboardThunkAction } from 'dashboard/redux/types';
import { ApiEndpoint, ApiModel } from 'lib/interfaces/api';

export function loadAccountStatuses(accountTypeId?: string) {
  const params = accountTypeId ? { filter: { 'account-type-id': accountTypeId } } : {};
  return async (dispatch: Dispatch) => {
    const accountStatusesResponse = await SessionClientDashboard.listResource<AccountStatus, {}>(
      ApiEndpoint.AccountStatus,
      params,
    );

    const accountStatuses = accountStatusesResponse.data;
    const accountType = accountStatusesResponse.included?.find(
      (apiRelationship) => apiRelationship.type === ApiModel.AccountType,
    ) as AccountType;

    dispatch(setAccountStatuses(accountStatuses, accountType));
  };
}

export const addAccountStatus =
  (name: string, accountTypeId?: string): DashboardThunkAction<Promise<void>> =>
  async (dispatch) => {
    const { data: accountStatus } = await SessionClientDashboard.postResourceCreate<
      AccountStatus,
      {
        type: ApiModel.AccountStatus;
        attributes: { name: string; 'account-type-id': string | undefined };
      }
    >({
      type: ApiModel.AccountStatus,
      attributes: { name, 'account-type-id': accountTypeId },
    });

    dispatch(addAccountStatuses([accountStatus]));
  };

export const renameAccountStatus =
  (accountStatusId: string, name: string): DashboardThunkAction<Promise<void>> =>
  async (dispatch) => {
    const accountStatusResponse = await SessionClientDashboard.updateResource<
      AccountStatus,
      {
        type: ApiModel.AccountStatus;
        id: string;
        attributes: { name: string };
      }
    >({
      type: ApiModel.AccountStatus,
      id: accountStatusId,
      attributes: { name },
    });

    dispatch(updateAccountStatus(accountStatusResponse.data));
  };

export const archiveAccountStatus =
  (accountStatusId: string): DashboardThunkAction<Promise<void>> =>
  async (dispatch) => {
    const accountStatusResponse = await SessionClientDashboard.postResourceAction<AccountStatus>(
      {
        type: ApiModel.AccountStatus,
        id: accountStatusId,
      },
      'archive',
    );

    dispatch(updateAccountStatus(accountStatusResponse.data));
  };

export const restoreAccountStatus =
  (accountStatusId: string): DashboardThunkAction<Promise<void>> =>
  async (dispatch) => {
    const accountStatusResponse = await SessionClientDashboard.postResourceAction<AccountStatus>(
      {
        type: ApiModel.AccountStatus,
        id: accountStatusId,
      },
      'restore',
    );

    dispatch(updateAccountStatus(accountStatusResponse.data));
  };

export const setDefaultGraphQueryTemplate =
  (accountTypeId: string, graphQueryTemplateId: string): DashboardThunkAction<Promise<void>> =>
  async (dispatch) => {
    await SessionClientDashboard.updateResource<AccountType>(
      {
        type: ApiModel.AccountType,
        id: accountTypeId,
      },
      { meta: { 'default-graph-query-template-id': graphQueryTemplateId } },
    );

    dispatch(updateDefaultGraphQueryTemplate(graphQueryTemplateId));
  };
